import { useLocation } from "wouter";
import { ChevronIcon } from "../design/Icons";
import { palette } from "../design/tokens";
import { fmt, t } from "../locale";

/**
 * The imprint and the privacy page. plan.md §15.
 *
 * They share a layout because they are the same kind of document: text a player
 * reads once, on a phone, usually because they went looking for it. Same
 * restraint as `HowToPlay` — no game furniture, no colour, nothing that
 * competes with the words.
 *
 * Every string lives in `en.json`. Legal text is exactly the kind that gets
 * quietly reworded in a component and then no longer matches what the site
 * actually says elsewhere.
 */

/** Stated on the privacy page so a reader can tell whether it is current. */
const PRIVACY_UPDATED = "1 August 2026";

const Page = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const [, navigate] = useLocation();

  // Every other screen fits one viewport by design and the app deliberately
  // does not scroll. These two do not fit and must, so they carry their own
  // scroll container rather than relying on the page.
  return (
    <div data-prose className="h-full overflow-y-auto">
      <div className="mx-auto flex max-w-md flex-col px-5 pb-12 pt-6">
        <button
          onClick={() => navigate("/")}
          className="flex min-h-0 min-w-0 items-center gap-1 self-start py-2 pr-2 text-sm text-ink-dim"
        >
          <ChevronIcon direction="left" color={palette.inkDim} size={16} />
          {t.auth.back}
        </button>

        <h1 className="mt-4 text-2xl">{title}</h1>

        {children}
      </div>
    </div>
  );
};

const Section = ({
  title,
  body,
}: {
  title: string;
  body: string | readonly string[];
}) => (
  <section className="mt-7">
    <h2 className="text-base font-bold">{title}</h2>
    {(typeof body === "string" ? [body] : body).map((paragraph) => (
      <p key={paragraph} className="mt-2 text-sm leading-relaxed text-ink-dim">
        {paragraph}
      </p>
    ))}
  </section>
);

/** A link between the two pages, so neither is a dead end. */
const CrossLink = ({ to, label }: { to: string; label: string }) => {
  const [, navigate] = useLocation();

  return (
    <button
      onClick={() => navigate(to)}
      className="mt-10 min-h-0 min-w-0 self-start text-sm underline underline-offset-4 text-ink-dim"
    >
      {label}
    </button>
  );
};

export const Imprint = () => (
  <Page title={t.imprint.title}>
    <section className="mt-7">
      <h2 className="text-base font-bold">{t.imprint.operatorTitle}</h2>
      <p className="mt-2 text-sm leading-relaxed text-ink-dim">
        {t.imprint.name}
        <br />
        {t.imprint.location}
      </p>
    </section>

    <section className="mt-7">
      <h2 className="text-base font-bold">{t.imprint.contactTitle}</h2>
      <p className="mt-2 text-sm leading-relaxed text-ink-dim">
        <a
          href={`mailto:${t.imprint.email}`}
          className="underline underline-offset-4"
        >
          {t.imprint.email}
        </a>
      </p>
    </section>

    <Section title={t.imprint.natureTitle} body={t.imprint.natureBody} />
    <Section title={t.imprint.liabilityTitle} body={t.imprint.liabilityBody} />

    <CrossLink to="/privacy" label={t.imprint.privacyLink} />
  </Page>
);

export const Privacy = () => (
  <Page title={t.privacy.title}>
    <p className="mt-1 text-xs text-ink-dim">
      {fmt(t.privacy.updated, PRIVACY_UPDATED)}
    </p>

    <p className="mt-5 text-sm leading-relaxed">{t.privacy.intro}</p>

    <Section
      title={t.privacy.controllerTitle}
      body={t.privacy.controllerBody}
    />
    <Section title={t.privacy.guestTitle} body={t.privacy.guestBody} />
    <Section title={t.privacy.accountTitle} body={t.privacy.accountBody} />
    <Section title={t.privacy.technicalTitle} body={t.privacy.technicalBody} />
    <Section title={t.privacy.statsTitle} body={t.privacy.statsBody} />
    <Section title={t.privacy.neverTitle} body={t.privacy.neverBody} />
    <Section
      title={t.privacy.retentionTitle}
      body={t.privacy.retentionBody}
    />
    <Section
      title={t.privacy.processorsTitle}
      body={t.privacy.processorsBody}
    />
    <Section title={t.privacy.rightsTitle} body={t.privacy.rightsBody} />
    <Section title={t.privacy.ageTitle} body={t.privacy.ageBody} />
    <Section title={t.privacy.securityTitle} body={t.privacy.securityBody} />
    <Section title={t.privacy.changesTitle} body={t.privacy.changesBody} />

    <CrossLink to="/imprint" label={t.privacy.imprintLink} />
  </Page>
);
